// client/src/pages/results/ResultPortalDisplay.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../../api/api';
import './resultPortal.css';
import { toast } from 'react-toastify';
import { SERVER_URL } from '../../constants/server-type';


function resolveDownloadUrl(downloadUrl) {

  if (!downloadUrl) {
    return null;
  }

  // Already a complete URL, such as an S3 signed URL.
  if (
    downloadUrl.startsWith('http://') ||
    downloadUrl.startsWith('https://')
  ) {
    return downloadUrl;
  }

  // Relative backend URL.
  return `${SERVER_URL}${downloadUrl.startsWith('/') ? '' : '/'}${downloadUrl}`;

}

function lightenColor(hex, percent = 25) {

  if (!hex) return '#ffffff';

  const num =
    parseInt(
      hex.replace('#', ''),
      16
    );

  let r =
    (num >> 16) + percent;

  let g =
    ((num >> 8) & 0xff) +
    percent;

  let b =
    (num & 0xff) +
    percent;

  r = Math.min(255, r);
  g = Math.min(255, g);
  b = Math.min(255, b);

  return (
    '#' +
    (
      (1 << 24) +
      (r << 16) +
      (g << 8) +
      b
    )
      .toString(16)
      .slice(1)
  );

}

export default function ResultPortalDisplay() {

  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('output');
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const cardBg = portal?.bg;  //lightenColor( portal?.bg, 35);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

    useEffect(() => { document.body.classList.add('results-fullscreen');

      return () => {
        document.body.classList.remove(
          'results-fullscreen'
        );
      };
    }, []);

  useEffect(() => {
    loadPortal();
  }, [token]);

  useEffect(() => {

    const handleKey =
      (e) => {

        if (
          previewIndex === null
        ) return;

        if (
          e.key === 'ArrowLeft' &&
          previewIndex > 0
        ) {

          setPreviewIndex(
            p => p - 1
          );

        }

        if (
          e.key === 'ArrowRight' &&
          previewIndex <
          items.length - 1
        ) {

          setPreviewIndex(
            p => p + 1
          );

        }

        if (
          e.key === 'Escape'
        ) {

          setPreviewIndex(
            null
          );

        }

      };

    window.addEventListener(
      'keydown',
      handleKey
    );

    return () =>
      window.removeEventListener(
        'keydown',
        handleKey
      );

  }, [
    previewIndex,
    items.length
  ]);

  function triggerDownload(url) {
    if (!url) {
      throw new Error(
        'The server did not return a download URL.'
      );
    }

    const link =
      document.createElement('a');

    link.href = url;
    link.style.display = 'none';

    // The server/S3 Content-Disposition header
    // should provide the actual ZIP filename.
    link.setAttribute('download', '');

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }


  async function loadPortal() {

    try {
      setLoading(true);
      const data = await apiFetch(`/results/public/${token}`);
      setPortal(data.portal);
      setItems(data.items || []);
      setViewMode(data.portal?.allowOutputs === false ? 'photo' : 'output');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const filteredItems = items;

  function getImageUrl(item) {

    if (
      viewMode === 'photo'
    ) {

      return item.photoUrl;

    }

    return (
      item.outputUrl ||
      item.photoUrl
    );

  }

  function toggleSelected( id, index, shiftKey = false ) {
      if (shiftKey && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const idsToAdd = filteredItems.slice(start,end + 1).map(x => x._id);
        setSelectedIds(prev => Array.from(new Set([ ...prev,  ...idsToAdd])));
      } else {
        setSelectedIds(prev => {
          if (prev.includes(id)) {
            return prev.filter(x => x !== id);
          }
          return [ ...prev, id];});
      }
      setLastSelectedIndex(index);
  }


  async function shareSelected() {
    try {
      const result = await toast.promise(

          apiFetch(
            '/results/share',
            {
              method: 'POST',
              body: {

                portalId:
                  portal._id,
                sourceToken: token,

                datadocIds:
                  selectedIds

              }
            }
          ),

          {
            pending:
              'Creating share...',
            success:
              'Share created.',
            error:
              'Share failed.'
          }

        );

      const url =
        `${window.location.origin}/results/share/${result.token}`;

      await navigator
        .clipboard
        .writeText(
          url
        );

      toast.success(
        'Link copied to clipboard'
      );

    } catch (err) {

      console.error(
        err
      );

    }

  }

  function downloadUrl(url) {

    const a =
      document.createElement(
        'a'
      );

    a.href = url;
    a.target = '_blank';

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

  }

  async function copyPortalLink() {

    try {

      await navigator
        .clipboard
        .writeText(
          window.location.href
        );

      toast.success(
        'Portal link copied'
      );

    } catch (err) {

      toast.error(
        'Unable to copy link'
      );

    }

  }

  async function downloadSelected() {

    const toastId =
      toast.loading(
        `Preparing archive (${selectedIds.length} image(s))...`
      );

    try {

      const result =
        await apiFetch(
          '/results/download',
          {
            method: 'POST',
            body: {
              token,
              ids: selectedIds,
              mode: viewMode
            }
          }
        );

      if (!result?.downloadUrl) {

        throw new Error(
          'The archive was created, but no download URL was returned.'
        );

      }

      const downloadUrl =
        result.downloadUrl.startsWith('http://') ||
        result.downloadUrl.startsWith('https://')
          ? result.downloadUrl
          : `${SERVER_URL}${result.downloadUrl.startsWith('/') ? '' : '/'}${result.downloadUrl}`;

      console.log(
        '[ResultPortalDisplay downloadSelected]',
        {
          returnedUrl:
            result.downloadUrl,

          resolvedUrl:
            downloadUrl
        }
      );

      toast.update(
        toastId,
        {
          render:
            'Archive ready. Starting download...',

          type:
            'success',

          isLoading:
            false,

          autoClose:
            3000,

          closeButton:
            true
        }
      );

      window.location.assign(
        downloadUrl
      );

      setSelectedIds([]);

    } catch (err) {

      console.error(
        '[ResultPortalDisplay downloadSelected]',
        err
      );

      toast.update(
        toastId,
        {
          render:
            err?.message ||
            'Download failed.',

          type:
            'error',

          isLoading:
            false,

          autoClose:
            5000,

          closeButton:
            true
        }
      );

    }

  }


  async function downloadAll() {

    const toastId =
      toast.loading(
        `Preparing archive (${filteredItems.length} image(s))...`
      );

    try {

      const result =
        await apiFetch(
          '/results/download',
          {
            method: 'POST',
            body: {
              token,
              mode: viewMode
            }
          }
        );

      if (!result?.downloadUrl) {

        throw new Error(
          'The archive was created, but no download URL was returned.'
        );

      }

      const downloadUrl =
        result.downloadUrl.startsWith('http://') ||
        result.downloadUrl.startsWith('https://')
          ? result.downloadUrl
          : `${SERVER_URL}${result.downloadUrl.startsWith('/') ? '' : '/'}${result.downloadUrl}`;

      console.log(
        '[ResultPortalDisplay downloadAll]',
        {
          returnedUrl:
            result.downloadUrl,

          resolvedUrl:
            downloadUrl
        }
      );

      toast.update(
        toastId,
        {
          render:
            'Archive ready. Starting download...',

          type:
            'success',

          isLoading:
            false,

          autoClose:
            3000,

          closeButton:
            true
        }
      );

      window.location.assign(
        downloadUrl
      );

      setSelectedIds([]);

    } catch (err) {

      console.error(
        '[ResultPortalDisplay downloadAll]',
        err
      );

      toast.update(
        toastId,
        {
          render:
            err?.message ||
            'Download failed.',

          type:
            'error',

          isLoading:
            false,

          autoClose:
            5000,

          closeButton:
            true
        }
      );

    }

  }


  if (loading) {

    return (
      <div
        className="results-loading"
      >
        Loading...
      </div>
    );

  }


  if (!portal) return <div role="alert">This results link is unavailable or has expired.</div>;

  const previewItem =
    previewIndex !== null
      ? filteredItems[previewIndex]
      : null;

  const previewUrl =
    previewItem
      ? getImageUrl(previewItem)
      : null;


  return (

    <div
      className="results-root"
      style={{
        background:
          portal?.bg || '#ffffff',
        minHeight: '100vh'
      }}
    >

      <div
        className="results-header"
      >

        <h1>
          {
            portal?.name ||
            'Results Portal'
          }
        </h1>

        <div
          className="results-toolbar"
        >

          {portal?.allowOriginals && (

            <button
              className={
                viewMode ===
                'photo'
                  ? 'btn-blue'
                  : 'btn-gray'
              }
              onClick={() =>
                setViewMode(
                  'photo'
                )
              }
            >
              Original Photos
            </button>

          )}

          {portal?.allowOutputs && (

            <button
              className={
                viewMode ===
                'output'
                  ? 'btn-blue'
                  : 'btn-gray'
              }
              onClick={() =>
                setViewMode(
                  'output'
                )
              }
            >
              Finished Products
            </button>

          )}

        </div>

      </div>

      <div
        className="results-toolbar"
      >

        <div
          className="results-toolbar-left"
        >

          <button
            className="btn-gray"
            onClick={() =>
              setSelectedIds(
                filteredItems.map(
                  x => x._id
                )
              )
            }
          >
            Select All
          </button>

          <button
            className="btn-gray"
            onClick={() =>
              setSelectedIds([])
            }
          >
            Clear
          </button>
          <button
            className="btn-blue"
            disabled={
              selectedIds.length === 0
            }
            onClick={
              shareSelected
            }
          >
            Share Selected
          </button>
        </div>

        <div
          className="results-toolbar-center"
        >
          {filteredItems.length} Photos
        </div>

        <div
          className="results-toolbar-right"
        >

          <button
            className="btn-green"
            onClick={downloadSelected}
            disabled={
              selectedIds.length === 0 || portal.allowDownload === false
            }
          >
            Download Selected (
            {selectedIds.length}
            )
          </button>

          <button
            className="btn-blue"
            onClick={downloadAll}
            disabled={portal.allowDownload === false}
          >
            Download All
          </button>
          <button
            className="btn-blue"
            onClick={copyPortalLink}
          >
            Copy Portal Link
          </button>
        </div>

      </div>
      <div
        className="results-grid"
      >

        {filteredItems.map(
          (item, index) => {

            const url =
              getImageUrl(
                item
              );

            if (!url)
              return null;

            return (

              <div
                key={item._id}
                className={
                  selectedIds.includes(item._id)
                    ? 'result-card selected'
                    : 'result-card'
                }
              >
                <input
                  className="result-checkbox"
                  type="checkbox"
                  checked={selectedIds.includes(item._id)}
                  onChange={(e) => toggleSelected(item._id, index, e.nativeEvent.shiftKey)}
                />

                <img
                  src={url}
                  alt=""
                  style={{
                    cursor: 'pointer'
                  }}
                  onClick={() =>
                    setPreviewIndex(index)
                  }
                />

              </div>

            );

          }
        )}

      </div>
      {previewItem && (

        <div
          className="results-modal-backdrop"
          onClick={() =>
            setPreviewIndex(null)
          }
        >

          <div
            className="results-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="results-modal-image-wrap">

              <img
                src={previewUrl}
                alt=""
                onClick={() =>
                  setPreviewIndex(null)
                }
              />

            </div>

            <div className="results-modal-toolbar">

              <button
                className="btn-gray"
                disabled={previewIndex === 0}
                onClick={() =>
                  setPreviewIndex(
                    Math.max(0, previewIndex - 1)
                  )
                }
              >
                Previous
              </button>

              <button
                  className="btn-green"
                  disabled={portal.allowDownload === false}
                  onClick={() =>
                    downloadUrl(previewUrl)
                }
              >
                Download Current
              </button>

              <button
                className="btn-gray"
                disabled={
                  previewIndex === filteredItems.length - 1
                }
                onClick={() =>
                  setPreviewIndex(
                    Math.min(
                      filteredItems.length - 1,
                      previewIndex + 1
                    )
                  )
                }
              >
                Next
              </button>

            </div>

          </div>

        </div>

      )}
    </div>
  );

}
